/** @fileoverview Coverage legend showing how many KPIs use each energy source. */
import { useMemo } from "react";
import { normaliseSource } from "./helpers";

/**
 * Summary chips of source usage across the currently selected KPIs.
 * @param {object} props Component props.
 * @param {Array<{id:number,name:string}>} props.sources Available energy sources.
 * @param {Record<number, number>} props.kpiSourceMap Per-KPI source assignments.
 * @param {Set<number>} props.pendingIds Currently selected KPI ids.
 * @returns {JSX.Element|null} The legend, or null when nothing to show.
 */
export default function SourceLegend({ sources, kpiSourceMap, pendingIds }) {
  const usage = useMemo(() => {
    const map = new Map();
    for (const id of pendingIds) {
      const sourceId = kpiSourceMap[id];
      if (!sourceId) continue;
      map.set(sourceId, (map.get(sourceId) || 0) + 1);
    }
    return map;
  }, [pendingIds, kpiSourceMap]);

  const unassignedCount = useMemo(() =>
    Array.from(pendingIds).filter(id => !kpiSourceMap[id]).length,
    [pendingIds, kpiSourceMap]
  );

  if (usage.size === 0 && unassignedCount === 0) return null;

  const normSources = (sources || []).map(normaliseSource);
  return (
    <div style={{ background: "var(--es-amber-50)", border: "1px solid var(--es-amber-300)", borderRadius: 10, padding: "10px 16px", marginBottom: 12, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
      <span style={{ fontSize: 10, fontWeight: 800, color: "var(--es-amber-900)", textTransform: "uppercase", letterSpacing: "0.5px" }}>⚡ Source Coverage:</span>
      {Array.from(usage.entries()).map(([sourceId, count]) => {
        const src = normSources.find(d => d.id === sourceId);
        return (
          <span key={sourceId} style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: "var(--es-white)", border: "1px solid var(--es-amber-300)", color: "var(--es-amber-900)" }}>
            {src?.name || `Source #${sourceId}`} · <strong>{count}</strong> KPI{count !== 1 ? "s" : ""}
          </span>
        );
      })}
      {unassignedCount > 0 && (
        <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: "var(--es-slate-100)", border: "1px solid var(--es-slate-200)", color: "var(--es-slate-400)" }}>
          No Source · <strong>{unassignedCount}</strong> KPI{unassignedCount !== 1 ? "s" : ""}
        </span>
      )}
    </div>
  );
}
